"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.subscribePolymarketOrderbook = subscribePolymarketOrderbook;
exports.unsubscribePolymarketOrderbook = unsubscribePolymarketOrderbook;
exports.fetchPolymarketOrderbook = fetchPolymarketOrderbook;
exports.fetchPolymarketBestBidAsk = fetchPolymarketBestBidAsk;
exports.startPolymarketOrderbookPolling = startPolymarketOrderbookPolling;
const api_1 = require("./api");
const auth_1 = require("./auth");
/**
 * Subscribe to Polymarket orderbook for given asset IDs (clobTokenIds).
 * The server will manage WebSocket connection to Polymarket.
 *
 * @param assetIds Array of clob token IDs [yesTokenId, noTokenId]
 */
async function subscribePolymarketOrderbook(assetIds) {
    try {
        console.log("🔵 subscribePolymarketOrderbook called with:", assetIds, "Type:", typeof assetIds, "IsArray:", Array.isArray(assetIds));
        const sessionToken = (0, auth_1.getSessionToken)();
        if (!sessionToken) {
            throw new Error("Not authenticated. Please login.");
        }
        const body = { assetIds };
        console.log("🔵 Request body:", JSON.stringify(body, null, 2));
        const response = await fetch(`${(0, api_1.getApiBaseUrl)()}/polymarket/orderbook/subscribe`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Session-Token": sessionToken,
            },
            body: JSON.stringify(body),
        });
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to subscribe to Polymarket orderbook: ${errorText}`);
        }
    }
    catch (error) {
        console.error("Error subscribing to Polymarket orderbook:", error);
        throw error;
    }
}
/**
 * Unsubscribe from Polymarket orderbook for given asset IDs.
 *
 * @param assetIds Array of clob token IDs to unsubscribe from
 */
async function unsubscribePolymarketOrderbook(assetIds) {
    try {
        const sessionToken = (0, auth_1.getSessionToken)();
        if (!sessionToken) {
            console.warn("No session token for unsubscribe");
            return;
        }
        const response = await fetch(`${(0, api_1.getApiBaseUrl)()}/polymarket/orderbook/unsubscribe`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Session-Token": sessionToken,
            },
            body: JSON.stringify({ assetIds }),
        });
        if (!response.ok) {
            console.error("Failed to unsubscribe from Polymarket orderbook");
        }
    }
    catch (error) {
        console.error("Error unsubscribing from Polymarket orderbook:", error);
    }
}
/**
 * Fetch current orderbook state from server cache.
 * Server maintains WebSocket and caches orderbook snapshots.
 *
 * @param assetId The clob token ID (typically YES token)
 * @returns Orderbook data with bids/asks, or null if not available
 */
async function fetchPolymarketOrderbook(assetId) {
    try {
        const sessionToken = (0, auth_1.getSessionToken)();
        if (!sessionToken) {
            throw new Error("Not authenticated. Please login.");
        }
        const response = await fetch(`${(0, api_1.getApiBaseUrl)()}/polymarket/orderbook?assetId=${encodeURIComponent(assetId)}`, {
            headers: {
                "X-Session-Token": sessionToken,
            },
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch Polymarket orderbook: ${response.statusText}`);
        }
        const data = await response.json();
        return data;
    }
    catch (error) {
        console.error("Error fetching Polymarket orderbook:", error);
        return null;
    }
}
/**
 * Fetch best bid/ask for a Polymarket asset.
 * More lightweight than full orderbook.
 *
 * @param assetId The clob token ID
 * @returns { bid, ask } or null
 */
async function fetchPolymarketBestBidAsk(assetId) {
    try {
        const sessionToken = (0, auth_1.getSessionToken)();
        if (!sessionToken) {
            throw new Error("Not authenticated. Please login.");
        }
        const response = await fetch(`${(0, api_1.getApiBaseUrl)()}/polymarket/orderbook/best-bid-ask?assetId=${encodeURIComponent(assetId)}`, {
            headers: {
                "X-Session-Token": sessionToken,
            },
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch best bid/ask: ${response.statusText}`);
        }
        const data = await response.json();
        return data;
    }
    catch (error) {
        console.error("Error fetching Polymarket best bid/ask:", error);
        return null;
    }
}
/**
 * Start polling Polymarket orderbook updates.
 * Unlike Kalshi which polls REST, Polymarket uses WebSocket on server.
 * This function subscribes once, then polls the cached data.
 *
 * @param assetIds Array of clob token IDs [yesTokenId, noTokenId]
 * @param callback Function to call with orderbook updates
 * @param intervalMs Polling interval (default 500ms - higher than Kalshi since updates are real-time via WebSocket)
 * @returns Cleanup function to stop polling and unsubscribe
 */
function startPolymarketOrderbookPolling(assetIds, callback, intervalMs = 500) {
    console.log("🟢 startPolymarketOrderbookPolling called with assetIds:", assetIds, "Type:", typeof assetIds, "IsArray:", Array.isArray(assetIds), "Length:", assetIds?.length);
    let intervalId = null;
    let timeoutId = null;
    let isSubscribed = false;
    let isCleaned = false; // Track if cleanup has been called
    let attemptCount = 0;
    const maxAttempts = 20; // Try for 10 seconds (20 * 500ms) before giving up
    // Subscribe to WebSocket on server
    subscribePolymarketOrderbook(assetIds)
        .then(() => {
        // Check if cleanup was called while we were subscribing
        if (isCleaned) {
            console.log(`⚠️ Cleanup called before subscription completed, unsubscribing immediately`);
            unsubscribePolymarketOrderbook(assetIds).catch(console.error);
            return;
        }
        isSubscribed = true;
        console.log(`✅ Subscribed to Polymarket orderbook for assets:`, assetIds);
        // Wait a bit before starting to poll to give WebSocket time to send initial snapshot
        timeoutId = window.setTimeout(() => {
            // Check again if cleanup was called during the timeout
            if (isCleaned) {
                console.log(`⚠️ Cleanup called during initial delay, not starting polling`);
                return;
            }
            console.log(`🔄 Starting to poll orderbook data for asset:`, assetIds[0]);
            // Start polling cached orderbook data (YES token typically)
            intervalId = window.setInterval(async () => {
                // Check if cleaned up before doing any work
                if (isCleaned) {
                    console.log(`⚠️ Polling interval fired but already cleaned, skipping`);
                    return;
                }
                if (assetIds.length > 0) {
                    attemptCount++;
                    const orderbook = await fetchPolymarketOrderbook(assetIds[0]);
                    // Check again after async fetch
                    if (isCleaned) {
                        console.log(`⚠️ Cleaned during fetch, not calling callback`);
                        return;
                    }
                    if (orderbook) {
                        if (attemptCount > 1) {
                            console.log(`✅ Orderbook data received after ${attemptCount} attempts`);
                        }
                        callback(orderbook);
                        attemptCount = 0; // Reset counter on success
                    }
                    else if (attemptCount <= maxAttempts) {
                        console.log(`⏳ Waiting for orderbook data... (attempt ${attemptCount}/${maxAttempts})`);
                    }
                    else if (attemptCount === maxAttempts + 1) {
                        console.warn(`⚠️ No orderbook data after ${maxAttempts} attempts. WebSocket may not have sent snapshot yet.`);
                    }
                }
            }, intervalMs);
        }, 1000); // Wait 1 second before starting to poll
    })
        .catch((error) => {
        console.error("Failed to start Polymarket orderbook polling:", error);
    });
    // Return cleanup function
    return () => {
        console.log(`🔴 Cleaning up Polymarket orderbook polling for assets:`, assetIds);
        isCleaned = true;
        // Clear the initial delay timeout if it hasn't fired yet
        if (timeoutId !== null) {
            console.log(`🔴 Clearing initial delay timeout`);
            clearTimeout(timeoutId);
            timeoutId = null;
        }
        // Clear the polling interval if it's running
        if (intervalId !== null) {
            console.log(`🔴 Clearing polling interval`);
            clearInterval(intervalId);
            intervalId = null;
        }
        // Unsubscribe from WebSocket if we successfully subscribed
        if (isSubscribed) {
            console.log(`🔴 Unsubscribing from Polymarket orderbook for assets:`, assetIds);
            unsubscribePolymarketOrderbook(assetIds).catch(console.error);
        }
    };
}
